import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type Stripe from 'stripe';

import type {
  StatsBillingCustomerSchema,
  StatsBillingSettingsSchemaNullable,
} from '@iconik-io/iconik-api/src/services/stats';

import { emptyUntilLoaded } from '../../store/empty_slice_state';
import type {
  BillingInformationAppState,
  BillingInformationCountry,
} from './types';

const initialState: BillingInformationAppState = {
  isLoading: false,
  invoices: {
    items: [],
    loading: false,
    isLoading: false,
    hasMore: false,
  },
  receiptsLoading: [],
  billingSettings: emptyUntilLoaded<StatsBillingSettingsSchemaNullable>(),
  billingSettingsUpdating: false,
  countries: [],
  billingInfoServerErrors: null,
  stripeConfigured: false,
  billingInfoUpdateProgress: false,
};

export const billingInformationSlice = createSlice({
  name: 'billing_information',
  initialState,
  reducers: {
    billingInfoLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    billingCountriesReceived: (
      state,
      action: PayloadAction<BillingInformationCountry[]>,
    ) => {
      state.countries = action.payload;
    },
    billingInfoReceived: (state, action: PayloadAction<Stripe.Customer>) => {
      state.customer = action.payload;
      state.isLoading = false;
    },
    billingInfoUpdated: (
      state,
      _action: PayloadAction<StatsBillingCustomerSchema>,
    ) => {
      state.isLoading = false;
    },
    billingInfoErrorsReceived: (
      state,
      action: PayloadAction<Record<string, string[]>>,
    ) => {
      state.billingInfoServerErrors = action.payload;
    },
    billingInfoUpdateProgress: (state, action: PayloadAction<boolean>) => {
      state.billingInfoUpdateProgress = action.payload;
    },
    billingInvoicesReset: (state) => {
      state.invoices = initialState.invoices;
    },
    billingInvoicesLoading: (state, action: PayloadAction<boolean>) => {
      state.invoices.isLoading = action.payload;
    },
    billingInvoicesReceived: (
      state,
      action: PayloadAction<{ data: Stripe.Invoice[]; has_more: boolean }>,
    ) => {
      state.invoices = {
        items: state.invoices.items.concat(action.payload.data),
        hasMore: action.payload.has_more,
        isLoading: false,
      };
    },
    billingInvoicesReceiptLoading: (state, action: PayloadAction<string>) => {
      state.receiptsLoading = state.receiptsLoading.concat(action.payload);
    },
    billingInvoicesReceiptLoaded: (state, action: PayloadAction<string>) => {
      state.receiptsLoading = state.receiptsLoading.filter(
        (id) => id !== action.payload,
      );
    },
    billingInfoEmailsReceived: (
      state,
      action: PayloadAction<string[] | undefined>,
    ) => {
      state.billingEmails = action.payload || [];
      state.billingEmailsUpdating = false;
    },
    billingInfoEmailsUpdating: (state, action: PayloadAction<boolean>) => {
      state.billingEmailsUpdating = action.payload;
    },
    billingSettingsReceived: (
      state,
      action: PayloadAction<StatsBillingSettingsSchemaNullable>,
    ) => {
      state.billingSettings = action.payload ?? {};
      state.billingSettingsUpdating = false;
    },
    billingSettingsUpdating: (state, action: PayloadAction<boolean>) => {
      state.billingSettingsUpdating = action.payload;
    },
  },
});

export const {
  billingCountriesReceived,
  billingInfoLoading,
  billingInfoReceived,
  billingInfoUpdated,
  billingInfoErrorsReceived,
  billingInfoUpdateProgress,
  billingInfoEmailsReceived,
  billingInfoEmailsUpdating,
  billingSettingsReceived,
  billingSettingsUpdating,
  billingInvoicesLoading,
  billingInvoicesReceived,
  billingInvoicesReset,
  billingInvoicesReceiptLoading,
  billingInvoicesReceiptLoaded,
} = billingInformationSlice.actions;

export const billingInformationReducer = billingInformationSlice.reducer;
